import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { WARRANTY_TABLE_ID } from '../constants';

import { approveWarrantyClaim, getWarrantyClaim, rejectWarrantyClaim } from './api';
import type { RejectWarrantyClaimValues } from './types';

/** Query-key factory for the warranty sub-module — one key space shared by the detail query and both
 *  decision mutations. The listing itself is keyed by `<DataTable>` off its `tableId`, not by this
 *  factory. */
export const warrantyKeys = {
  all: ['warranty-claims'] as const,
  detail: (id: string) => [...warrantyKeys.all, 'detail', id] as const,
  /** Device filter typeahead, keyed by search term so a new term resets the cursor. */
  metaDevices: (search: string) => [...warrantyKeys.all, 'meta', 'devices', search] as const,
};

/** One warranty claim's detail; idle while `id` is empty, so a closed drawer fetches nothing. */
export function useWarrantyClaim(id: string) {
  return useQuery({
    queryKey: warrantyKeys.detail(id),
    queryFn: () => getWarrantyClaim(id),
    enabled: Boolean(id),
  });
}

/** Both decisions bust the same caches. The real backend's approve/reject responses are slim
 *  (`{id, status}`), never a detail, so the claim's detail is **invalidated** rather than seeded —
 *  an open drawer refetches and shows the new status (and, for a rejection, its Rejection Reason).
 *
 *  **Two roots**, because `<DataTable>` keys the list off its `tableId` (`['warranty', …]`) which
 *  `warrantyKeys.all` (`['warranty-claims']`) does not prefix-match; miss one and the row keeps
 *  showing Pending. One helper so the two mutations cannot drift. */
function syncDecisionCaches(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: warrantyKeys.detail(id) });
  void qc.invalidateQueries({ queryKey: warrantyKeys.all });
  void qc.invalidateQueries({ queryKey: [WARRANTY_TABLE_ID] });
}

/** Rejects the claim with the admin's reason. Per-claim hook: the reject modal renders for one claim,
 *  so the id is known at hook time. */
export function useRejectWarrantyClaim(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: RejectWarrantyClaimValues) => rejectWarrantyClaim(id, values),
    onSuccess: () => syncDecisionCaches(qc, id),
  });
}

/** Approves the claim. The id is a **mutate argument** here, not a hook argument: approve fires from
 *  an imperative `useConfirm()` dialog that any row's kebab can open, so it is only known at call
 *  time. */
export function useApproveWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveWarrantyClaim(id),
    onSuccess: (_result, id) => syncDecisionCaches(qc, id),
  });
}
